import {AlertTriangleIcon} from '@components/ui/icon'
import {useTranslation} from '@/translations/i18n.tsx'
import {useAssignment} from '@features/assignment/queries/useAssignment'
import {useURLParsedParams} from '@hooks/useURLParsedParams.ts'
import {ErrorBox} from '@components/commons/error-box'
import {AxiosError} from 'axios'
import {Container} from '@components/ui/container'
import {Spinner} from '@components/ui/spinner'
import {Helmet} from 'react-helmet'
import {AssignmentHero} from '@features/assignment/components/assignment-hero'
import {AssignmentContent} from '@features/assignment/components/assignment-content'
import {Assignment as AssignmentType, AssignmentUrlParams} from '@features/assignment/types.ts'
import {useEffect} from 'react'
import {useChatbotStore} from '@features/chatbot/store/chatbotStore.ts'
import {useRequiredActions} from '@features/home/queries/useRequiredActions'
import {useParsedSearchParams} from '@hooks/useParsedSearchParams'
import {AssignmentsURLParams} from '@features/home/types'
import {useTableCounts} from '@features/home/queries/useTableCounts'
import {useHomeAssignments} from '@features/home/queries/useHomeAssignments'
import {TASK_CATEGORY_REVIEW} from '@features/assignment/utils.tsx'

const checkIsAssignmentNotAvailable = (
    assignmentStatus: AssignmentType['status'],
    taskStatus: AssignmentType['task_status']['status'],
    coordinationStatus: AssignmentType['task_status']['coordination_status'],
    isReview: boolean
) => {
    const notAvailableAssignmentStatuses = [
        'soft_cancelled',
        'auto_cancelled',
        'hard_cancelled',
        'declined'
    ] as const satisfies AssignmentType['status'][]
    // Completed reviews stay visible, showing their last step as completed, instead of the "not available" page.
    const notAvailableTaskStatuses = (
        [
            'soft_cancelled',
            'auto_cancelled',
            'hard_cancelled',
            'overdue',
            'completed'
        ] as const satisfies AssignmentType['task_status']['status'][]
    ).filter(status => !(isReview && status == 'completed'))
    return (
        notAvailableAssignmentStatuses.some(status => status == assignmentStatus) ||
        notAvailableTaskStatuses.some(status => status == taskStatus) ||
        coordinationStatus == 'cancellation_requested'
    )
}

export const Assignment = () => {
    const {t} = useTranslation()

    const {searchParams} = useParsedSearchParams(AssignmentsURLParams)
    useRequiredActions()
    useTableCounts({search: searchParams.search})
    useHomeAssignments(searchParams)

    const urlParams = useURLParsedParams(AssignmentUrlParams)
    const setAssignment = useChatbotStore(store => store.setAssignment)
    const assignmentQuery = useAssignment(urlParams.success ? urlParams.data.code : undefined, {
        enabled: urlParams.success
    })

    useEffect(() => {
        if (assignmentQuery.isSuccess) {
            setAssignment(assignmentQuery.data)
        }
    }, [assignmentQuery.data])

    if (assignmentQuery.isPending) {
        return (
            <Container fullHeight className="flex items-center justify-center text-neutral-400">
                <Spinner size={40} />
            </Container>
        )
    }
    if (
        !urlParams.success ||
        (assignmentQuery.error instanceof AxiosError && assignmentQuery.error.response?.status == 404)
    ) {
        return (
            <Container>
                <ErrorBox icon={<AlertTriangleIcon />} title={t('assignment:not_found')} />
            </Container>
        )
    }
    if (assignmentQuery.error || !assignmentQuery.data) {
        return (
            <Container>
                <ErrorBox icon={<AlertTriangleIcon />} title={t('errors:default')} />
            </Container>
        )
    }
    if (
        (assignmentQuery.data.assignment_logic == 'First Accept' ||
            assignmentQuery.data.task_category.name == TASK_CATEGORY_REVIEW) &&
        assignmentQuery.data.status == 'auto_cancelled'
    ) {
        return (
            <main className="flex flex-col min-w-0">
                <Helmet
                    title={t('assignment:meta_title', {
                        title: assignmentQuery.data.entity.title ?? assignmentQuery.data.entity.external_id
                    })}
                />
                <AssignmentHero assignment={assignmentQuery.data} />
            </main>
        )
    }
    if (
        checkIsAssignmentNotAvailable(
            assignmentQuery.data.status,
            assignmentQuery.data.task_status.status,
            assignmentQuery.data.task_status.coordination_status,
            assignmentQuery.data.task_category.name == TASK_CATEGORY_REVIEW
        )
    ) {
        return (
            <Container>
                <ErrorBox icon={<AlertTriangleIcon />} title={t('assignment:not_available')} />
            </Container>
        )
    }

    return (
        <main className="flex flex-col min-w-0">
            <Helmet
                title={t('assignment:meta_title', {
                    title: assignmentQuery.data.entity.title ?? assignmentQuery.data.entity.external_id
                })}
            />
            <AssignmentHero assignment={assignmentQuery.data} />
            <AssignmentContent assignment={assignmentQuery.data} />
            {/*<AssignmentRatingModal />*/}
        </main>
    )
}
